import React, { useState, useEffect, useRef } from "react";
import { formatStoryDate } from "../../../helper/utils";

import { object, bool } from "prop-types";
import { throttle } from "lodash";
import { useSelector } from "react-redux";
import get from "lodash/get";
import Author from "../../../atoms/author";
import FollowUs from "../../../atoms/follow-story-page";
import BookmarkCommentShareButtons from "../../../atoms/bookmark-comment-share-buttons";
import { AdContainer } from "../../../ads";
import { getTotalParaCount } from "../../story-elements";

import "./author-and-timestamp.m.css";

const AuthorAndTimeStamp = ({ story, isPVExclusive }) => {
  const hiddenAuthorIds = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "hidden_author_ids"], [])
  );
  const isAdfree = useSelector((state) => get(state, ["qt", "config", "subscriber"]) === "true");
  const hiddenIdsList = Array.isArray(hiddenAuthorIds)
    ? hiddenAuthorIds.map(String)
    : hiddenAuthorIds
    ? [String(hiddenAuthorIds)]
    : [];
  const bylineAuthorName = story?.metadata?.["byline-no-author-page"];

  const primaryAuthorId = get(story, ["authors", "0", "id"], null);
  const isPrimaryAuthorHidden = primaryAuthorId ? hiddenIdsList.includes(String(primaryAuthorId)) : false;

  const authorsToDisplay = story.authors
    ? story.authors.filter((author) => !hiddenIdsList.includes(String(author.id)))
    : [];
  const hasVisibleAuthors = isPrimaryAuthorHidden
    ? false
    : authorsToDisplay.length > 0 || (bylineAuthorName && bylineAuthorName.length > 0);

  const [isMobile, setMobile] = useState(false);
  const [stickyFollowUs, setStickyFollowUs] = useState(false);
  const [followusWidth, setFollowusWidth] = useState("auto");
  const followusRef = useRef(null);

  useEffect(() => {
    if (window && window.innerWidth < 1024) {
      setMobile(true);
    } else {
      setMobile(false);
    }
  }, []);

  useEffect(() => {
    // Keep the story-page follow/share row in normal document flow on mobile.
    // Its scroll-driven sticky state conflicts with the mobile header animation.
    if (isMobile) {
      setStickyFollowUs(false);
      const stickyShare = document.querySelector(".sticky-share");
      if (stickyShare) {
        stickyShare.style.top = "";
        stickyShare.classList.remove("move-down-sticky-share");
      }
      return undefined;
    }

    const desktopHeader = document.querySelector("#desktop-sticky-header");
    const timestampWrapper = document.querySelector("#timestamp-container");
    const exclusiveWrapper = document.querySelector("#exclusive-author-wrapper");
    const stickyShare = document.querySelector(".sticky-share");
    const stickyShareTop = stickyShare ? stickyShare.getBoundingClientRect().top + window.pageYOffset : 0;

    let previousScrollY = window.scrollY;

    function followusBarStickyHandler() {
      if (typeof window === "undefined") return;
      const currentScrollY = window.scrollY;
      const scrollPosition = window.scrollY + window.innerHeight;
      const pageHeight = document.documentElement.scrollHeight;

      const isAtPageBottom = scrollPosition >= pageHeight;
      const desktopHeaderBottom = desktopHeader?.getBoundingClientRect().bottom;
      const timestampBottom = timestampWrapper?.getBoundingClientRect().bottom;
      const exclusiveTop = exclusiveWrapper?.getBoundingClientRect().top;

      const taboolaAdWrapper = document.querySelector("#ad-wrapper-taboola");
      const tagsContainer = document.querySelector("#tags-container");

      if (tagsContainer) {
        let tagsContainerTop = tagsContainer.getBoundingClientRect().top;
        tagsContainerTop -= isMobile ? 10 : isPVExclusive ? 60 : 20;

        const stickyShareHeight = stickyShare?.getBoundingClientRect().height || 0;

        if (tagsContainerTop <= stickyShareHeight) {
          setStickyFollowUs(false);
          previousScrollY = currentScrollY;
          return;
        }
      }

      if (!["listicle", "live-blog"].includes(story["story-template"]) && taboolaAdWrapper) {
        let taboolaAdTop = taboolaAdWrapper.getBoundingClientRect().top;
        taboolaAdTop -= isMobile ? 60 : isPVExclusive ? 180 : 120;

        const stickyShareHeight = stickyShare?.getBoundingClientRect().height || 0;

        if (taboolaAdTop <= stickyShareHeight) {
          setStickyFollowUs(false);
          previousScrollY = currentScrollY;
          return;
        }
      }

      if (isAtPageBottom) {
        setStickyFollowUs(true);
      } else if (isMobile) {
        if (currentScrollY >= stickyShareTop) {
          setStickyFollowUs(true);
          if (stickyShare) {
            stickyShare.style.top = "0px";
            stickyShare.classList.remove("move-down-sticky-share");
            if (currentScrollY < previousScrollY) {
              stickyShare.classList.add("move-down-sticky-share");
            }
          }
        } else {
          if (currentScrollY < previousScrollY && currentScrollY <= stickyShareTop - 35) {
            setStickyFollowUs(false);
          }
        }
      } else if (isPVExclusive) {
        setStickyFollowUs(desktopHeaderBottom >= exclusiveTop);
      } else {
        setStickyFollowUs(desktopHeaderBottom >= timestampBottom);
      }
      previousScrollY = currentScrollY;
    }

    const throttledFollowusBarStickyHandler = throttle(followusBarStickyHandler, 50);
    window.addEventListener("scroll", throttledFollowusBarStickyHandler);

    return () => {
      window.removeEventListener("scroll", throttledFollowusBarStickyHandler);
      throttledFollowusBarStickyHandler.cancel();
    };
  }, [isMobile, isPVExclusive]);

  useEffect(() => {
    if (followusRef.current) {
      setFollowusWidth(`${followusRef.current.offsetWidth}px`);
    }
  }, [stickyFollowUs, isMobile]);

  const publishedDateAndTime = formatStoryDate(story["first-published-at"]);

  const lastUpdatedDateAndTime = formatStoryDate(story["last-published-at"]);

  const tempHideAd =
    story?.["story-template"] === "brandspot-with-no-ad" ||
    story?.["story-template"] === "exclusive-story-without-ad";

  const AuthorTimeStampAndIcons = (
    <div styleName="wrapper">
      {hasVisibleAuthors && (
        <div styleName="author-name">
          <Author authors={authorsToDisplay} story={story} clazzName="author-name-image" />
        </div>
      )}
      <div styleName="timestamp" id="timestamp-container">
        <>
          <div styleName="date">Published : {publishedDateAndTime} IST</div>
          {story["owner-name"].toLowerCase() !== "migrator" && (
            <>
              <div styleName="short-line"></div>
              <div styleName="date">Last Updated : {lastUpdatedDateAndTime} IST</div>
            </>
          )}
        </>
      </div>
      {!isAdfree && !tempHideAd ? (
        <div styleName="ad-wrapper" className="hide-desktop">
          <AdContainer
            AdClassName="standard-300x250"
            id="div-gpt-ad-1770621030464-0"
            desktop={false}
            mobile="PV_Mweb_AT_Top_300x250"
            cta={getTotalParaCount(story) > 0 ? "inStory" : "sidebar"}
          />
        </div>
      ) : null}
      <div className={`${stickyFollowUs ? "alternateDiv-Sticky" : ""}`}></div>
      <div
        styleName="follow-us-wrapper"
        className={`sticky-share ${stickyFollowUs ? "sticky-followus" : ""}`}
        ref={followusRef}
        style={{
          width: `${followusWidth}`,
          paddingRight: `0px`
        }}
      >
        <div styleName="follow-us-left">
          <span>ಫಾಲೋ ಮಾಡಿ</span>
          <FollowUs />
        </div>
        <div>
          <BookmarkCommentShareButtons isPVExclusive={isPVExclusive} story={story} />
        </div>
      </div>
    </div>
  );

  const ExclusiveAuthorAndTimeStamp = (
    <>
      <div className={`${stickyFollowUs ? "alternateDiv-Sticky-exclusive" : ""}`}></div>
      <div
        className={`exclusive-author-wrapper ${stickyFollowUs ? "sticky-exclusive-wrapper" : ""}`}
        styleName="exclusive-author-wrapper"
        id="exclusive-author-wrapper"
        style={{ width: followusWidth }}
        ref={followusRef}
      >
        <div styleName="pvexclusive">
          <div styleName="follow-us-left exclusive-follow-us">
            <span>ಫಾಲೋ ಮಾಡಿ</span>
            <FollowUs isPVExclusive={isPVExclusive} />
          </div>
          <div styleName="long-line"></div>
          {hasVisibleAuthors && <Author authors={authorsToDisplay} story={story} clazzName="author-name-image" />}
          <div styleName="exclusive-timestamp">
            <div styleName="date">Published {publishedDateAndTime} IST</div>
            <div styleName="short-line"></div>
            <div styleName="date">Last Updated {lastUpdatedDateAndTime} IST</div>
          </div>
          <div styleName="long-line"></div>
          <BookmarkCommentShareButtons story={story} isPVExclusive={isPVExclusive} stickyFollowUs={stickyFollowUs} />
        </div>
      </div>
    </>
  );

  if (typeof window !== "undefined" && window.innerWidth >= 1024 && isPVExclusive) {
    return ExclusiveAuthorAndTimeStamp;
  }
  return AuthorTimeStampAndIcons;
};

AuthorAndTimeStamp.propTypes = {
  story: object.isRequired,
  isPVExclusive: bool
};

AuthorAndTimeStamp.defaultProps = {
  isPVExclusive: false
};

export default AuthorAndTimeStamp;
